// SPV Pool v1.0 — hand-written DTOs mirroring the API contract for the
// /api/spv-pools/* and /api/spvs/* controllers (PR 1-4).

export type SpvPoolStatus =
  | "Planning"
  | "Building"
  | "EligibilityReview"
  | "IDUPDrafting"
  | "InvestorMarketing"
  | "InvestorCommitment"
  | "Closing"
  | "Active"
  | "WoundDown";

export const SPV_POOL_STATUSES: readonly SpvPoolStatus[] = [
  "Planning",
  "Building",
  "EligibilityReview",
  "IDUPDrafting",
  "InvestorMarketing",
  "InvestorCommitment",
  "Closing",
  "Active",
  "WoundDown",
];

export interface SpvPoolDto {
  id: string;
  spvId: string;
  poolName: string;
  status: SpvPoolStatus;
  offeringType: string;
  inceptionDate: string;
  portfolioId: string | null;
  targetPoolSize: number;
  minSegmentCount: number;
  maxBorrowerConcentrationPct: number;
  weightedAvgCreditRatingTarget: string;
  closingDate: string | null;
  windDownDate: string | null;
  notes: string | null;
  createdAt: string;
  modifiedAt: string;
}

// SPV Pool v1.0 PR 8 §6.2 — stage-gate certifications.

export type CertificationRecordStatus =
  | "NotStarted"
  | "InProgress"
  | "Approved"
  | "Revoked";

export interface RequiredCertificationDto {
  certificationDefinitionId: string;
  name: string;
  description: string;
  stageFrom: string;
  stageTo: string;
  requiredRoles: string[];
  currentStatus: CertificationRecordStatus;
  currentRecordId: string | null;
}

export interface StageGateCheckResult {
  canAdvance: boolean;
  fromStatus: string;
  toStatus: string;
  required: RequiredCertificationDto[];
  missing: RequiredCertificationDto[];
  revoked: RequiredCertificationDto[];
}

export interface StageGateGroup {
  fromStatus: string;
  toStatus: string;
  gates: RequiredCertificationDto[];
}

export interface SpvPoolCreate {
  spvId: string;
  poolName: string;
  inceptionDate: string;
  portfolioId?: string | null;
  targetPoolSize: number;
  minSegmentCount: number;
  maxBorrowerConcentrationPct: number;
  weightedAvgCreditRatingTarget: string;
  offeringType: string;
  notes?: string | null;
}

export type SpvPoolUpdate = Partial<SpvPoolCreate>;

export interface SpvPoolListQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: SpvPoolStatus | string;
  spvId?: string;
}

export interface PoolTrancheFill {
  poolTrancheId: string;
  trancheCode: string;
  committedAmount: number;
  targetAmount: number;
  fillPct: number;
}

export interface PoolMath {
  spvPoolId: string;
  contributionPctSegments: number;
  contributionPctPrincipal: number;
  investmentFillPct: number;
  fundedFillPct: number;
  contributedSegmentCount: number;
  eligibleSegmentTotal: number;
  contributedPrincipal: number;
  perTrancheFill: PoolTrancheFill[];
}

export type PoolCompositionStatus = "Proposed" | "Committed" | "Contributed";

export interface PoolCompositionDto {
  id: string;
  spvPoolId: string;
  loanId: string;
  segmentId: string;
  segmentNumber: number;
  status: PoolCompositionStatus;
  currentPrincipalBalance: number;
  proposedAt: string | null;
  committedAt: string | null;
  contributedAt: string | null;
  notes: string | null;
}

export interface EligibleSegment {
  segmentId: string;
  segmentNumber: number;
  currentPrincipalBalance: number;
  alreadyPooled: boolean;
}

export interface EligibleLoanDto {
  loanId: string;
  loanNumber: string;
  borrowerName: string;
  creditRating: string;
  status: string;
  segments: EligibleSegment[];
}

export interface CompositionValidationRule {
  rule: string;
  passed: boolean;
  detail: string;
}

export interface CompositionValidationResult {
  passed: boolean;
  rules: CompositionValidationRule[];
}

export interface ContributeResult {
  contributed: number;
}

export interface PoolEligibilityCriteriaDto {
  id: string;
  spvPoolId: string;
  minSeasoningMonths: number;
  minCreditRating: string;
  maxDelinquencyDays: number;
  maxBorrowerConcentrationPct: number;
  minDSCR: number;
  maxSingleLoanPct: number;
  allowedGeographiesCsv: string;
  notes: string | null;
}

export type PoolEligibilityCriteriaUpdate = Omit<
  PoolEligibilityCriteriaDto,
  "id" | "spvPoolId"
>;

export type IDUPStatus =
  | "NotStarted"
  | "Drafting"
  | "InternalReview"
  | "Approved"
  | "Distributed";

export interface IDUPDocumentDto {
  id: string;
  spvPoolId: string;
  version: number;
  status: IDUPStatus;
  generatedReportId: string | null;
  sectionsJson: string | null;
  approvedAt: string | null;
  approvedById: string | null;
  distributedAt: string | null;
  createdAt: string;
}

// SPV Pool v1.0 PR 7 — rendered IDUP content for inline display.
export interface IDUPPreviewDto {
  idupDocumentId: string;
  version: number;
  generatedReportId: string;
  content: string;
}

// --- Cash Flow (PR 6) -----------------------------------------------------

export type CashFlowPeriod = "Monthly" | "Quarterly" | "Annual";

export const CASH_FLOW_PERIODS: readonly CashFlowPeriod[] = [
  "Monthly",
  "Quarterly",
  "Annual",
];

export interface CashFlowInflowsDto {
  totalInflows: number;
  loanCount: number;
}

export interface WaterfallRowDto {
  priority: number;
  name: string;
  amount: number;
  /** 0..1 fraction of total inflows. */
  pctOfInflows: number;
}

export interface CashFlowWaterfallDto {
  rows: WaterfallRowDto[];
  totalAmount: number;
}

export type ReserveFundEntryType = "TopUp" | "Draw" | "Adjustment";

export type ReserveFundEntrySource =
  | "WaterfallPosting"
  | "ManualAdjustment"
  | "AccountingImport";

export interface ReserveFundLedgerEntry {
  id: string;
  occurredAt: string;
  entryType: ReserveFundEntryType;
  source: ReserveFundEntrySource;
  amount: number;
  memo: string | null;
  relatedWaterfallDistributionId: string | null;
}

export interface ReserveFundDailyBalance {
  date: string;
  balance: number;
}

export interface ReserveFundDto {
  fundId: string | null;
  currentBalance: number;
  targetAmount: number;
  ledger: ReserveFundLedgerEntry[];
  dailyBalances: ReserveFundDailyBalance[];
}

export interface InvestorDistributionRow {
  investorId: string;
  investorName: string;
  netAmount: number;
}

export interface TrancheDistributionDto {
  trancheId: string;
  trancheCode: string;
  trancheName: string;
  distinctInvestorCount: number;
  totalNet: number;
  investors: InvestorDistributionRow[];
}

export interface InvestorDistributionsDto {
  tranches: TrancheDistributionDto[];
}

export interface PoolCashFlowDto {
  poolId: string;
  period: CashFlowPeriod;
  periodStart: string;
  periodEnd: string;
  inflows: CashFlowInflowsDto;
  waterfall: CashFlowWaterfallDto;
  reserveFund: ReserveFundDto;
  investorDistributions: InvestorDistributionsDto;
}

export interface AccountingStatusDto {
  isConnected: boolean;
  providerName: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
}

export interface ReserveManualAdjustmentRequest {
  amount: number;
  reason: string;
}

// --- SPV Trusts -----------------------------------------------------------

export type SpvStatus = "Forming" | "Operating" | "WindingDown" | "Closed";

export const SPV_STATUSES: readonly SpvStatus[] = [
  "Forming",
  "Operating",
  "WindingDown",
  "Closed",
];

export interface SpvDto {
  id: string;
  trustName: string;
  trustEntityId: string | null;
  stateOfFormation: string;
  formationDate: string;
  einMasked: string | null;
  trustCompanyId: string | null;
  indentureTrusteeId: string | null;
  backupServicerId: string | null;
  primaryPlacementAgentId: string | null;
  governingLawState: string;
  status: SpvStatus;
  notes: string | null;
  createdAt: string;
  modifiedAt: string;
}

export interface SpvCreate {
  trustName: string;
  stateOfFormation: string;
  formationDate: string;
  ein?: string | null;
  trustCompanyId: string | null;
  indentureTrusteeId?: string | null;
  backupServicerId?: string | null;
  primaryPlacementAgentId?: string | null;
  governingLawState: string;
  notes?: string | null;
}

export type SpvUpdate = Partial<SpvCreate>;

export interface SpvListQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: SpvStatus | string;
}

// --- Payment System v3.0 PR 6: pool formation + SSNs + segment triggers ---

export interface FinalizePoolRequest {
  formationDate: string; // ISO date (YYYY-MM-DD)
  expectedVersionId: number;
  confirmPoolName: string;
}

export interface FinalizePoolResponse {
  poolId: string;
  formationEventId: string;
  formationDate: string;
  rosterLoanCount: number;
  ssnsIssued: number;
  rufObligationsFired: number;
  sftfObligationsFired: number;
  newVersionId: number;
}

export interface PoolFormationEventDto {
  id: string;
  poolId: string;
  formationDate: string;
  formedByUserId: string | null;
  cssFiled: boolean;
  cssDocumentId: string | null;
  rosterLoanCount: number;
  notes: string | null;
  createdAt: string;
}

export interface PoolSegmentTriggerEventDto {
  id: string;
  poolId: string;
  loanId: string;
  segmentNumber: number;
  triggerSource: "pool_formation" | "investor_closing" | string;
  formationEventId: string | null;
  closingPaymentId: string | null;
  triggerDate: string;
  isReversed: boolean;
  createdAt: string;
}

export interface SsnDto {
  id: string;
  sellerId: string;
  poolId: string;
  segmentId: string;
  faceAmount: number;
  currentBalance: number;
  issueDate: string;
  isRetired: boolean;
}

// --- Payment System v3.0 PR 7: investor closings + VFI inflows -----------

/** Tier code wire values consumed by /closings/preview + /closings. */
export type ClosingTier = "tier_a" | "tier_b" | "tier_c1";

export const CLOSING_TIERS: readonly ClosingTier[] = [
  "tier_a",
  "tier_b",
  "tier_c1",
];

export interface SubscriptionInput {
  investorId: string;
  tier: ClosingTier | string;
  amount: number;
}

export interface ClosingPreviewRequest {
  closingDate: string; // ISO date
  subscriptions: SubscriptionInput[];
}

export interface ClosingExecuteRequest extends ClosingPreviewRequest {
  expectedVersionId: number;
}

export interface PerLoanSsnIssuance {
  loanId: string;
  loanNumber: string;
  sellerId: string;
  segmentNumber: number;
  ssnFaceAmount: number;
}

export interface ClosingDisbursementPreview {
  priorityPosition: number;
  obligationTypeCode: string;
  recipientResolution: string;
  recipientCounterpartyId: string | null;
  recipientDisplayName: string | null;
  amount: number;
}

export interface InvestorCommitmentPreview {
  investorId: string;
  investorDisplayName: string;
  tier: string;
  poolTrancheId: string;
  amount: number;
}

export interface ClosingPreview {
  grossProceeds: number;
  cumulativeInvestorCapitalBefore: number;
  cumulativeInvestorCapitalAfter: number;
  fillPercentageAfter: number;
  newlyTriggeredSegments: number[];
  ssnIssuances: PerLoanSsnIssuance[];
  rufFiringCount: number;
  sftfFiringCount: number;
  wf2Disbursements: ClosingDisbursementPreview[];
  commitmentPreviews: InvestorCommitmentPreview[];
  /**
   * Set when the waterfall split was REFUSED — a seller on the roster is
   * unpayable, or a priority's shares do not total 100%. `wf2Disbursements`
   * is empty and this holds the reason and the remedy, already written for an
   * operator. Executing in this state fails with the same message, so a
   * surface that renders this must also block its confirm.
   */
  wf2BlockedReason: string | null;
}

export interface ClosingExecuteResponse {
  paymentId: string;
  poolId: string;
  closingDate: string;
  newVersionId: number;
  cumulativeInvestorCapitalAfter: number;
  newlyTriggeredSegments: number[];
  ssnsIssuedCount: number;
  disbursementCount: number;
}

export interface ClosingSummary {
  paymentId: string;
  poolId: string;
  closingDate: string;
  grossProceeds: number;
  disbursementTotal: number;
  segmentTriggerCount: number;
  ssnsIssuedCount: number;
  status: string;
  notes: string | null;
}

export interface ClosingSubscriptionDetail {
  investorId: string;
  investorDisplayName: string;
  tier: string;
  poolTrancheId: string;
  amount: number;
}

export interface ClosingDisbursementDetail {
  priorityPosition: number;
  obligationTypeCode: string;
  recipientResolution: string;
  recipientCounterpartyId: string | null;
  recipientDisplayName: string | null;
  amount: number;
}

export interface ClosingDetailResponse {
  paymentId: string;
  poolId: string;
  closingDate: string;
  grossProceeds: number;
  status: string;
  notes: string | null;
  subscriptions: ClosingSubscriptionDetail[];
  disbursements: ClosingDisbursementDetail[];
  triggeredSegments: number[];
  ssnsIssued: PerLoanSsnIssuance[];
}

// --- VFI inflows ----------------------------------------------------------

export interface VfiAttributionInput {
  loanId: string;
  attributionPercent: number;
}

export interface VfiPreviewRequest {
  inflowDate: string;
  source: string;
  grossVfi: number;
  attributions: VfiAttributionInput[];
}

export interface VfiLayer1Split {
  loanId: string;
  loanNumber: string;
  attributionPercent: number;
  loanPortion: number;
  phoenixRetained: number;
  poolAttributable: number;
}

export interface VfiPreview {
  grossVfi: number;
  phoenixRetainedTotal: number;
  poolAttributableTotal: number;
  vmpAdminFee: number;
  netVfi: number;
  perLoanSplits: VfiLayer1Split[];
  wf2Disbursements: ClosingDisbursementPreview[];
  /** See {@link ClosingPreview.wf2BlockedReason} — same contract. Note that
   * with the waterfall refused there is no P1 line, so `vmpAdminFee` reads 0
   * and `netVfi` reads the whole pool-attributable total; neither figure is
   * meaningful while this is set. */
  wf2BlockedReason: string | null;
}

export interface VfiResult {
  paymentId: string;
  grossVfi: number;
  phoenixRetainedTotal: number;
  poolAttributableTotal: number;
  perLoanSplits: VfiLayer1Split[];
  disbursementCount: number;
}
